import React from 'react';
import { MouseEventHandler, useCallback, useState, useEffect, useRef } from "react";
import ConfirmationModal from '../modals/ConfirmationModal';
import { DeleteBinIcon } from '@liveoptics/react-ui';
import { Dispatch, SetStateAction } from "react";
import SortIndicatorDownArrow from '../svgs/SortIndicatorDownArrow';
import SortIndicatorUpArrow from '../svgs/SortIndicatorUpArrow'; 
import TrashCanIcon from '../svgs/TrashCanIcon'; 
import '../css/calc-custom.scss';

interface VmTableProps {
    data: any[];
    currentNumberOfVms: number;
    setCurrentNumberOfVms: any;
    viewerState: any;
    setViewerState: Dispatch<SetStateAction<any>>;
    setEnableGetPricingComparisonButton: any;
}

const VmConfigurationTable = (props: VmTableProps) => {
    const { data, currentNumberOfVms, setCurrentNumberOfVms, viewerState, setViewerState, setEnableGetPricingComparisonButton } = props;
    const [ showModal, setShowModal ] = useState<boolean>();
    const [ vmsToDeductFromCount, setVmsToDeductFromCount] = useState<number>(0);
    const [ rowIdToDelete, setRowIdToDelete ] = useState<string>();
    const [ applySort, setApplySort ] = useState<boolean>();
    
    type Data = typeof data;
    type SortKeys = keyof Data[0];
    type SortOrder = "asc" | "desc";

    const [sortKey, setSortKey] = useState<SortKeys>("vmName");
    const [sortOrder, setSortOrder] = useState<SortOrder>("asc");

    const sortedData = useCallback(
        () => sortData({tableData: data, sortKey, reverse: sortOrder === "desc"}),
        [data, sortKey, sortOrder]
    );

     const onSortStatusChange = useCallback((event) => {
        setApplySort(event.target.value);
     }, []);

    function sortData({
        tableData, sortKey, reverse,
    }: {
        tableData: Data;
        sortKey: SortKeys;
        reverse: Boolean;
    }) {
        if(!applySort) return tableData;

        if(!sortKey) return tableData;

        const sortedData = data.sort((a, b) => {
            return a[sortKey] > b[sortKey] ? 1 : -1;
        });

        if(reverse) {
            return sortedData.reverse();
        }

        return sortedData;
    }

    function SortButton({
        label,
        sortOrder,
        sortKey,
        columnKey,
        onClick,
    }: {
        label: string;
        sortOrder: SortOrder;
        sortKey: SortKeys;
        columnKey: SortKeys;
        onClick: MouseEventHandler<HTMLButtonElement>;
    }) {
        return (
            <button onClick={onClick} className="sort-button">
                <span className="button-label">{label}</span> {
                    sortKey === columnKey && sortOrder === "desc" ? <SortIndicatorDownArrow /> : <SortIndicatorUpArrow />
                }
            </button>
        );
    }

    function prepDeleteRow(vm) {
        setApplySort(false);
        setShowModal(true);
        setRowIdToDelete(vm.id);
        setVmsToDeductFromCount(vm.numberOfVMs);
    }

    function changeSort(key: SortKeys) {
        setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        setSortKey(key);
        setApplySort(true);
    }

    const deleteRow = () => {
        const newTableData = data;
        const index = data.findIndex((row) => row.id === rowIdToDelete);
        newTableData.splice(index, 1);
        
        setViewerState({
            ...viewerState,
            configuration: {
                ...viewerState.configuration,
                tableData: newTableData
            }
        });

        setCurrentNumberOfVms(currentNumberOfVms-vmsToDeductFromCount);
        setEnableGetPricingComparisonButton(true);
        console.log('deletRow2');
    }

    return (
        <>
            <div className="vm-configuration-table-wrapper">
                <table className="vm-configuration-table">
                    <thead>
                        <tr>
                            <th>
                                <SortButton
                                    label={"Name"}
                                    columnKey={"vmName"}
                                    onClick={() => changeSort("vmName")}
                                    {...{
                                        sortOrder,
                                        sortKey
                                    }}
                                />
                            </th>
                            <th>
                                <SortButton
                                    label={"Number of VMs "}
                                    columnKey={"numberOfVMs"}
                                    onClick={() => changeSort("numberOfVMs")}
                                    {...{
                                        sortOrder,
                                        sortKey
                                    }}
                                />
                                <span id="current-number-of-vms-badge">{currentNumberOfVms}</span>
                            </th>
                            <th>
                                <SortButton
                                    label={"vCPU / VM"}
                                    columnKey={"vCPUPerVM"}
                                    onClick={() => changeSort("vCPUPerVM")}
                                    {...{
                                        sortOrder,
                                        sortKey
                                    }}
                                />
                            </th>
                            <th>
                                <SortButton
                                    label={"Memory (GiB) / VM"}
                                    columnKey={"memoryPerVM"}
                                    onClick={() => changeSort("memoryPerVM")}
                                    {...{
                                        sortOrder,
                                        sortKey
                                    }}
                                />
                            </th>
                            <th>
                                <SortButton
                                    label={"Storage (GiB) / VM"}
                                    columnKey={"storagePerVM"}
                                    onClick={() => changeSort("storagePerVM")}
                                    {...{
                                        sortOrder,
                                        sortKey
                                    }}
                                />
                            </th>
                            <th>
                                <SortButton
                                    label={"OS Type"}
                                    columnKey={"osType"}
                                    onClick={() => changeSort("osType")}
                                    {...{
                                        sortOrder,
                                        sortKey
                                    }}
                                />
                            </th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedData().map((vm) => {
                            return (
                                <tr key={vm.id}>
                                    <td>{vm.vmName}</td>
                                    <td>{vm.numberOfVMs}</td>
                                    <td>{vm.vCPUPerVM}</td>
                                    <td>{vm.memoryPerVM}</td>
                                    <td>{vm.storagePerVM}</td>
                                    <td>{vm.osType}</td>
                                    <td>
                                        <span id={vm.id} className="pointer">
                                            <TrashCanIcon onClick={() => prepDeleteRow(vm)} />
                                        </span>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {
                showModal ? 
                    <ConfirmationModal 
                        modalTitle="Remove VM Configuration"
                        closeFn={() => {setShowModal(false)}}
                        deleteRow={() => {deleteRow()}}
                    /> : null
            }
        </>
    );
}

export default VmConfigurationTable;